import { TABLE_CELL_TYPE } from 'types/enums';
import type { ReactNode } from 'react';
import type { DataSchema, Schema } from 'types';
import type { HeliosRecordDataTableColumnSettings } from 'types/dataHelios.ts';
import type { ColumnAggregationState } from 'types/globalStore.ts';
import { formatDateWithMask } from './dateAndTime';
import { isRecord } from './typeChecks';

export type TableAggregationType = 'sum' | 'avg' | 'min' | 'max' | 'count';

export const NUMERIC_HELIOS_DATA_TYPES = new Set(['Int32', 'Int64', 'Decimal']);

export const AGGREGATION_OPTIONS: { value: TableAggregationType | null; labelKey: string }[] = [
  { value: null, labelKey: 'common.none' },
  { value: 'sum', labelKey: 'common.sum' },
  { value: 'avg', labelKey: 'common.average' },
  { value: 'min', labelKey: 'common.minimum' },
  { value: 'max', labelKey: 'common.maximum' },
  { value: 'count', labelKey: 'common.count' },
];

export interface TableAggregationRenderContext<TRow> {
  columnId: string;
  rows: TRow[];
  type: TableAggregationType;
  value: unknown;
}

export interface TableAggregationConfig<TRow> {
  type: TableAggregationType;
  getValue?: (row: TRow) => unknown;
  render?: (context: TableAggregationRenderContext<TRow>) => ReactNode;
  includeInFooter?: boolean;
  includeInGrouping?: boolean;
}

export type TableColumnAggregationState = ColumnAggregationState;

export const formDataToDataD = <T extends keyof P, P extends DataSchema>(formData: Record<string, unknown>): Schema<T, P>[] => {
  return Object.entries(formData).map(([rowId, fields]) => {
    return {
      identifier: rowId,
      id: rowId,
      ...(typeof fields === 'object' && fields !== null ? fields : {}),
    } as Schema<T, P>;
  });
};

export const getSelectRowId = <T extends { id: string; identifier: string }>(row: T, isHeliosTable: boolean) =>
  isHeliosTable ? row.identifier : row.id;

export const getCellGroupingValue = (type: TABLE_CELL_TYPE, cellValue: unknown) => {
  const value = isRecord(cellValue) && 'rawValue' in cellValue ? cellValue.rawValue : cellValue;

  if (type === TABLE_CELL_TYPE.DATE && Array.isArray(value)) {
    const [date, mask] = value;
    return formatDateWithMask(date, mask);
  }

  if (Array.isArray(value) && isRecord(value[0]) && 'value' in value[0]) {
    return value[0].value ?? '';
  }

  if (type === TABLE_CELL_TYPE.USERS && Array.isArray(value)) {
    return JSON.stringify(value.map((user) => (isRecord(user) && typeof user.fullName === 'string' ? user.fullName : '')));
  }

  return value;
};

export const getAggregationConfig = <TRow>(
  aggregation?: TableAggregationType | TableAggregationConfig<TRow> | null
): TableAggregationConfig<TRow> | null => {
  if (!aggregation) return null;

  return typeof aggregation === 'string' ? { type: aggregation } : aggregation;
};

const getDynamicRelationCount = (originalValue: unknown, value: unknown): number | null => {
  const originalCount =
    isRecord(originalValue) && typeof originalValue.dynamicRelationValuesCount === 'number' ? originalValue.dynamicRelationValuesCount : 0;

  if (Array.isArray(value)) {
    const containsStatusEntries = value.some((item) => isRecord(item) && typeof item.status === 'string');

    if (!containsStatusEntries) return value.length;

    return value.reduce((count, item) => {
      if (!isRecord(item) || typeof item.status !== 'string') {
        return count;
      }

      if (item.status === 'add') {
        return count + 1;
      }

      if (item.status === 'remove') {
        return count - 1;
      }

      return count;
    }, originalCount);
  }

  if (isRecord(value) && ('add' in value || 'remove' in value)) {
    const added = Array.isArray(value.add) ? value.add.length : 0;
    const removed = Array.isArray(value.remove) ? value.remove.length : 0;

    return originalCount + added - removed;
  }

  return null;
};

export const getAggregationFormValue = (
  originalValue: unknown,
  value: unknown,
  columnType?: TABLE_CELL_TYPE,
  aggregationType?: TableAggregationType
) => {
  if (value === undefined) return originalValue;

  const hasRawValue = isRecord(originalValue) && 'rawValue' in originalValue;
  const dynamicRelationCount = getDynamicRelationCount(originalValue, value);

  if (hasRawValue) {
    return {
      ...originalValue,
      rawValue: value,
      ...(dynamicRelationCount !== null ? { dynamicRelationValuesCount: Math.max(0, dynamicRelationCount) } : {}),
    };
  }

  if ((columnType === TABLE_CELL_TYPE.HELIOS_BROWSE || columnType === TABLE_CELL_TYPE.HELIOS_FILE) && value !== null && value !== undefined) {
    return {
      rawValue: value,
      ...(dynamicRelationCount !== null ? { dynamicRelationValuesCount: Math.max(0, dynamicRelationCount) } : {}),
    };
  }

  if (
    columnType === TABLE_CELL_TYPE.USERS &&
    aggregationType &&
    ['min', 'max'].includes(aggregationType) &&
    Array.isArray(originalValue) &&
    originalValue.length > 0
  ) {
    const userNames = originalValue
      .map((item) => (isRecord(item) && typeof item.fullName === 'string' ? item.fullName : null))
      .filter((item): item is string => Boolean(item));

    if (userNames.length > 1) {
      return userNames.reduce((selectedName, currentName) =>
        aggregationType === 'min'
          ? selectedName <= currentName
            ? selectedName
            : currentName
          : selectedName >= currentName
            ? selectedName
            : currentName
      );
    }

    return userNames[0];
  }

  return value;
};

const getAggregationComparableValue = (value: unknown) => {
  if (value === null || value === undefined || value === '') return null;

  if (Array.isArray(value) && value.length >= 1 && typeof value[0] === 'string') {
    const date = new Date(value[0]);
    if (!Number.isNaN(date.getTime())) {
      return date;
    }

    return value[0];
  }

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value === 'boolean') {
    return Number(value);
  }

  if (typeof value === 'string') {
    const trimmedValue = value.trim();

    if (!trimmedValue) return null;

    const numericValue = Number(trimmedValue.replace(',', '.'));
    if (Number.isFinite(numericValue)) {
      return numericValue;
    }

    const timestamp = Date.parse(trimmedValue);
    if (!Number.isNaN(timestamp)) {
      return new Date(timestamp);
    }

    return trimmedValue;
  }

  if (typeof value === 'object' && value !== null && 'rawValue' in value) {
    return getAggregationComparableValue(value.rawValue);
  }

  return null;
};

export const aggregateValues = (
  values: unknown[],
  type: TableAggregationType,
  columnType?: TABLE_CELL_TYPE,
  settings?: HeliosRecordDataTableColumnSettings
) => {
  const getStaticSelectionLabel = (value: unknown) => {
    if (columnType !== TABLE_CELL_TYPE.HELIOS_STATIC_SELECTION) {
      return value;
    }

    return settings?.staticSelection?.selectItems?.find((item) => item.key === String(value))?.value ?? value;
  };

  const getFileUploadLength = (value: unknown) => {
    if (typeof value === 'object' && value !== null && 'rawValue' in value) {
      return getFileUploadLength((value as { rawValue: unknown }).rawValue);
    }

    if (Array.isArray(value)) {
      return value.length;
    }

    return 0;
  };

  const getHeliosFileLength = (value: unknown) => {
    if (value === null || value === undefined || value === '') {
      return 0;
    }

    if (Array.isArray(value)) {
      const dynamicRelationCount = getDynamicRelationCount(undefined, value);
      return dynamicRelationCount ?? value.length;
    }

    if (typeof value !== 'object' || value === null) {
      return 1;
    }

    if ('dynamicRelationValuesCount' in value) {
      const dynamicRelationValuesCount = (value as { dynamicRelationValuesCount: unknown }).dynamicRelationValuesCount;

      if (typeof dynamicRelationValuesCount === 'number') {
        return dynamicRelationValuesCount;
      }
    }

    if ('rawValue' in value) {
      const rawValue = (value as { rawValue: unknown }).rawValue;

      const dynamicRelationCount = getDynamicRelationCount(undefined, rawValue);
      if (dynamicRelationCount !== null) {
        return Math.max(0, dynamicRelationCount);
      }

      if (typeof rawValue === 'object' && rawValue !== null && 'id' in rawValue) {
        const id = (rawValue as { id: unknown }).id;
        if (id !== null && id !== undefined) {
          return 1;
        }
      }

      if (rawValue !== null && rawValue !== undefined && rawValue !== '') {
        return 1;
      }
    }

    return 0;
  };

  if (columnType === TABLE_CELL_TYPE.FILE_UPLOAD) {
    const fileUploadLengths = values.map(getFileUploadLength);

    switch (type) {
      case 'count':
        return fileUploadLengths.reduce((total, length) => total + length, 0);

      case 'min':
        return fileUploadLengths.length ? Math.min(...fileUploadLengths) : null;

      case 'max':
        return fileUploadLengths.length ? Math.max(...fileUploadLengths) : null;

      default:
        break;
    }
  }

  if (columnType === TABLE_CELL_TYPE.HELIOS_FILE || columnType === TABLE_CELL_TYPE.HELIOS_BROWSE) {
    const fileUploadLengths = values.map(getHeliosFileLength);

    switch (type) {
      case 'count':
        return fileUploadLengths.reduce((total, length) => total + length, 0);

      case 'min':
        return fileUploadLengths.length ? Math.min(...fileUploadLengths) : null;

      case 'max':
        return fileUploadLengths.length ? Math.max(...fileUploadLengths) : null;

      default:
        break;
    }
  }

  if (type === 'count') {
    if (columnType === TABLE_CELL_TYPE.USERS) return values.flat().length;

    return values.filter((value) => {
      if (typeof value === 'object' && value !== null && 'rawValue' in value) {
        return (
          (value as { rawValue: unknown }).rawValue !== null &&
          (value as { rawValue: unknown }).rawValue !== undefined &&
          (value as { rawValue: unknown }).rawValue !== ''
        );
      }

      return value !== null && value !== undefined && value !== '';
    }).length;
  }

  const comparableValues = values.map(getAggregationComparableValue).filter((value): value is NonNullable<typeof value> => value !== null);

  if (!comparableValues.length) return null;

  switch (type) {
    case 'sum': {
      const numericValues = comparableValues.filter((value): value is number => typeof value === 'number');
      return numericValues.length ? numericValues.reduce((acc, value) => acc + value, 0) : null;
    }

    case 'avg': {
      const numericValues = comparableValues.filter((value): value is number => typeof value === 'number');
      return numericValues.length ? numericValues.reduce((acc, value) => acc + value, 0) / numericValues.length : null;
    }

    case 'min':
      return getStaticSelectionLabel(
        comparableValues.reduce(
          (lowest, value) => {
            if (lowest === null) return value;
            if (lowest instanceof Date && value instanceof Date) {
              return lowest.getTime() <= value.getTime() ? lowest : value;
            }
            return lowest <= value ? lowest : value;
          },
          null as string | number | Date | null
        )
      );

    case 'max':
      return getStaticSelectionLabel(
        comparableValues.reduce(
          (highest, value) => {
            if (highest === null) return value;
            if (highest instanceof Date && value instanceof Date) {
              return highest.getTime() >= value.getTime() ? highest : value;
            }
            return highest >= value ? highest : value;
          },
          null as string | number | Date | null
        )
      );

    default:
      return null;
  }
};

type TableCellEditabilityOptions = {
  isHeliosTable?: boolean;
  isEditableColumnsAllowed?: boolean;
  isRowInEditMode?: boolean;
};

export const isTableCellEditable = (rowId: string, isColumnEditable: boolean, options: TableCellEditabilityOptions = {}): boolean => {
  const isNewRow = rowId.startsWith('new');
  const isRowInEditMode = Boolean(options.isRowInEditMode);

  if (options.isHeliosTable) {
    return (isRowInEditMode || isNewRow) && isColumnEditable;
  }

  return (Boolean(options.isEditableColumnsAllowed) && isColumnEditable) || isRowInEditMode || isNewRow;
};
